// @ts-nocheck
import { request } from '@umijs/max';

const USE_DIRECT_CONNECTION = false // 生产必须为 false:走同源 /api,由宿主机 nginx 代理到后端 8000
const DIRECT_BASE_URL = 'http://1.94.49.218:8080';

/**
 * 获取完整的URL
 * @param path API路径
 * @returns 完整的URL
 */
export const getUrl = (path: string) => USE_DIRECT_CONNECTION ? `${DIRECT_BASE_URL}${path}` : path;

/**
 * 处理token过期的辅助函数
 * @param error 错误对象
 */
export const handleTokenExpiration = (error: any) => {
  if (error.response && error.response.status === 401) {
    console.log('Token已过期或无效，清除本地存储的token');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // 可以在这里添加重定向到登录页的逻辑
    // 但为了避免循环依赖，让调用方处理重定向
  }
  throw error;
};

/**
 * 获取认证headers
 * @param additionalHeaders 额外的headers
 * @returns 包含认证信息的headers对象
 */
export const getAuthHeaders = (additionalHeaders: Record<string, string> = {}) => {
  const token = localStorage.getItem('token');
  return {
    // 统一带 Bearer 前缀，与后端 JWT 校验格式一致（后端两种写法都兼容）
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...additionalHeaders,
  };
};

/**
 * 请求基类
 * 提供统一的请求方法，自动处理token和认证
 */
export class RequestBase {
  /**
   * 发送GET请求
   * @param url 请求URL
   * @param params 查询参数
   * @param options 请求选项
   * @returns Promise<T>
   */
  static async get<T = any>(
    url: string, 
    params?: Record<string, any>, 
    options?: { [key: string]: any }
  ): Promise<T> {
    try {
      // 处理responseType选项
      const requestOptions: any = {
        method: 'GET',
        headers: getAuthHeaders(),
        params,
        ...(options || {}),
        skipErrorHandler: USE_DIRECT_CONNECTION,
      };
      
      // 如果有responseType选项，需要特殊处理
      if (options?.responseType) {
        requestOptions.responseType = options.responseType;
        // 对于blob类型响应，我们需要不同的返回处理
        if (options.responseType === 'blob') {
          const response = await request(getUrl(url), requestOptions);
          return response as unknown as T;
        }
      }
      
      return await request<T>(getUrl(url), requestOptions);
    } catch (error: any) {
      handleTokenExpiration(error);
      throw error;
    }
  }

  /**
   * 发送POST请求
   * @param url 请求URL
   * @param data 请求数据
   * @param options 请求选项
   * @returns Promise<T>
   */
  static async post<T = any>(
    url: string, 
    data?: any, 
    options?: { [key: string]: any }
  ): Promise<T> {
    try {
      return await request<T>(getUrl(url), {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        data,
        ...(options || {}),
        skipErrorHandler: USE_DIRECT_CONNECTION,
      });
    } catch (error: any) {
      handleTokenExpiration(error);
      throw error;
    }
  }

  /**
   * 发送PUT请求
   * @param url 请求URL
   * @param data 请求数据
   * @param options 请求选项
   * @returns Promise<T>
   */
  static async put<T = any>(
    url: string, 
    data?: any, 
    options?: { [key: string]: any }
  ): Promise<T> {
    try {
      return await request<T>(getUrl(url), {
        method: 'PUT',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        data,
        ...(options || {}),
        skipErrorHandler: USE_DIRECT_CONNECTION,
      });
    } catch (error: any) {
      handleTokenExpiration(error);
      throw error;
    }
  }

  /**
   * 发送DELETE请求
   * @param url 请求URL
   * @param options 请求选项
   * @returns Promise<T>
   */
  static async delete<T = any>(
    url: string, 
    options?: { [key: string]: any }
  ): Promise<T> {
    try {
      return await request<T>(getUrl(url), {
        method: 'DELETE',
        headers: getAuthHeaders(),
        ...(options || {}),
        skipErrorHandler: USE_DIRECT_CONNECTION,
      });
    } catch (error: any) {
      handleTokenExpiration(error);
      throw error;
    }
  }

  /**
   * 发送PATCH请求
   * @param url 请求URL
   * @param data 请求数据
   * @param options 请求选项
   * @returns Promise<T>
   */
  static async patch<T = any>(
    url: string, 
    data?: any, 
    options?: { [key: string]: any }
  ): Promise<T> {
    try {
      return await request<T>(getUrl(url), {
        method: 'PATCH',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        data,
        ...(options || {}),
        skipErrorHandler: USE_DIRECT_CONNECTION,
      });
    } catch (error: any) {
      handleTokenExpiration(error);
      throw error;
    }
  }

  /**
   * 发送自定义请求
   * @param url 请求URL
   * @param config 请求配置
   * @returns Promise<T>
   */
  static async request<T = any>(
    url: string, 
    config: { [key: string]: any }
  ): Promise<T> {
    try {
      const headers = getAuthHeaders(config.headers || {});
      return await request<T>(getUrl(url), {
        ...config,
        headers,
        skipErrorHandler: USE_DIRECT_CONNECTION,
      });
    } catch (error: any) {
      handleTokenExpiration(error);
      throw error;
    }
  }

  /**
   * 发送不需要认证的GET请求（用于登录等接口）
   * @param url 请求URL
   * @param params 查询参数
   * @param options 请求选项
   * @returns Promise<T>
   */
  static async getWithoutAuth<T = any>(
    url: string, 
    params?: Record<string, any>, 
    options?: { [key: string]: any }
  ): Promise<T> {
    try {
      return await request<T>(getUrl(url), {
        method: 'GET',
        params,
        ...(options || {}),
        skipErrorHandler: USE_DIRECT_CONNECTION,
      });
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * 发送不需要认证的POST请求（用于登录等接口）
   * @param url 请求URL
   * @param data 请求数据
   * @param options 请求选项
   * @returns Promise<T>
   */
  static async postWithoutAuth<T = any>(
    url: string, 
    data?: any, 
    options?: { [key: string]: any }
  ): Promise<T> {
    try {
      return await request<T>(getUrl(url), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data,
        ...(options || {}),
        skipErrorHandler: USE_DIRECT_CONNECTION,
      });
    } catch (error: any) {
      throw error;
    }
  }
}

/**
 * 导出常用的工具函数
 */

/**
 * 默认导出RequestBase类
 */
export default RequestBase;
